/*
 * When the person last did something the back office's server counts as activity (ADR 0008 §4:
 * 30 minutes without it close the session): any operation but `auth.state`, which lib/trpc.ts
 * records here as its answer arrives. The other tabs of the back office in this browser hear of
 * it, so that a tab left alone does not warn about a session another tab keeps in use. Only a
 * time travels between tabs, nothing of the person.
 */

const CHANNEL = 'eklevo-staff-activity'

interface ActivityMessage {
  type: 'activity'
  at: number
}

let last = Date.now()
const listeners = new Set<(at: number) => void>()

/** The time of the last activity the server has counted (milliseconds since the epoch). */
export function lastActivity(): number {
  return last
}

function update(at: number): void {
  if (at <= last) return
  last = at
  for (const listener of [...listeners]) listener(at)
}

/** An operation counted as activity has just been answered, in this tab. */
export function recordActivity(at = Date.now()): void {
  update(at)
  if (typeof BroadcastChannel === 'undefined') return
  const channel = new BroadcastChannel(CHANNEL)
  channel.postMessage({ type: 'activity', at } satisfies ActivityMessage)
  channel.close()
}

/** Calls `listener` with each newer activity, of this tab or another; returns the unsubscribe. */
export function onActivity(listener: (at: number) => void): () => void {
  listeners.add(listener)
  if (typeof BroadcastChannel === 'undefined') return () => listeners.delete(listener)
  const channel = new BroadcastChannel(CHANNEL)
  channel.onmessage = (e: MessageEvent) => {
    const message = e.data as Partial<ActivityMessage> | null
    if (message?.type === 'activity' && typeof message.at === 'number') update(message.at)
  }
  return () => {
    listeners.delete(listener)
    channel.close()
  }
}

/** For the tests: starts the clock again at `at`. */
export function resetActivity(at = Date.now()): void {
  last = at
}
